import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import {
  addSelectItem,
  beautify,
  createColumn,
  createViewAction,
  formatAmount,
  formatDateTime,
  getRegister,
  itemExists,
  numberFormat,
  useGetUserPermissions,
} from "../../utils/helpers";
import { URL_NEGOTIATED_PAYMENTS } from "../../utils/urls";
import { IFormSection } from "../../types/common";
import { useLocation } from "react-router-dom";
import { Table } from "../../components/common/Table";
import { Link } from "react-router-dom";
import {
  API_COLLECT_PAYMENT,
  API_FULL_PAYMENTS,
  API_NEGOTIATIONS,
  API_PAYMENT_MODES,
} from "../../utils/apis";
import {
  PaymentStatusTag,
  StatusTag,
} from "../../components/common/registerUtils";
import useSet from "../common/useSet";
import toast from "react-hot-toast";
import { Button, Modal } from "antd";
import {
  APPROVE,
  CAN_APPROVE_NEGOTIATED_PAYMENT,
  CAN_REQUEST_NEGOTIATED_PAYMENT,
  MOBILE_MONEY,
  REJECT,
} from "../../utils/constants";
import useGet from "../common/useGet";
import { CheckCircleOutlined } from "@mui/icons-material";

export const useNegotiations = () => {
  const { uploadData, success, error } = useSet();
  const permissions = useGetUserPermissions();

  const handleApprove = (id: any) => {
    Modal.confirm({
      title: "Confirm Approval",
      content: (
        <div>
          <p>Are you sure you want to approve this request?</p>
        </div>
      ),
      okText: "Yes, Approve",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await uploadData({
            api: `${API_NEGOTIATIONS}${id}/approve/`,
            method: "POST",
          });
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } catch (error) {
          toast.error("Failed to approve request");
        }
      },
    });
  };

  const handleReject = (id: any) => {
    Modal.confirm({
      title: "Confirm Rejection",
      content: (
        <div>
          <p>Are you sure you want to reject this request?</p>
        </div>
      ),
      okText: "Yes, Reject",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await uploadData({
            api: `${API_NEGOTIATIONS}${id}/reject/`,
            method: "POST",
          });

          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } catch (error) {
          toast.error("Failed to reject request");
        }
      },
    });
  };

  useEffect(() => {
    if (success) {
      toast.success(`Action successful`);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    if (Object?.keys(error)?.length > 0) {
      toast.error(error?.detail || "Failed to approval");
    }
  }, [success, error]);
  const tableColumns = () => [
    createColumn("car_number", true, "Car Number"),
    createColumn("negotiated_amount", false, "Negotiated Amount"),
    createColumn("total_amount", false, "Total Amount"),
    createColumn("status", true, "Status"),
    createColumn("created_by", true, "Created By"),
    createColumn("created_at", false, "Created At"),
    createColumn("payment", false, "Payment"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        total_amount: numberFormat(item?.total_amount),
        negotiated_amount: numberFormat(item?.negotiated_amount),
        status: <StatusTag status={item?.status} />,
        created_at: formatDateTime(item.created_at),
        created_by: beautify(item.created_by_name),

        payment: (
          <div className="flex gap-2">
            {item?.reason === "Write Off" && item?.status === "approved" && (
              <PaymentStatusTag status="Written Off" />
            )}
            {item?.consolidated_invoice_details?.payment_status === "paid" && (
              <PaymentStatusTag
                status={item?.consolidated_invoice_details?.payment_status}
              />
            )}

            {item?.status === "approved" &&
              item?.consolidated_invoice_details?.payment_status !== "paid" &&
              item?.reason !== "Write Off" && (
                <Link
                  className="link-with-icon"
                  to={`/make_payments/create`}
                  state={{ item }}
                >
                  Make Payment
                </Link>
              )}
          </div>
        ),
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_NEGOTIATED_PAYMENTS, item?.id),
              ...(itemExists(permissions, [CAN_APPROVE_NEGOTIATED_PAYMENT]) &&
              item.status === "pending"
                ? [
                    {
                      label: APPROVE,
                      custom: true,
                      handleCustom: () => handleApprove(item?.id),
                    },
                    {
                      label: REJECT,
                      custom: true,
                      handleCustom: () => handleReject(item?.id),
                    },
                  ]
                : []),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          // title: "Actions Details",
          fields: [],
          component: (
            <div className="flex justify-content-end gap-2">
              {itemExists(permissions, [CAN_APPROVE_NEGOTIATED_PAYMENT]) &&
                item.status === "pending" && (
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<CheckCircleOutlined style={{ fontSize: "14px" }} />}
                    onClick={() => handleApprove(item?.id)}
                  >
                    Approve
                  </Button>
                )}
            </div>
          ),
        },
        {
          title: "Negotiated Payment Details",
          fields: {
            id: item?.id,
            consolidated_invoice: item?.consolidated_invoice,
            invoices: item?.invoices?.length
              ? item.invoices.map((inv, i) => (
                  <span key={inv.id}>
                    <PaymentStatusTag
                      status={
                        inv?.invoice_number
                          ? inv.invoice_number.toUpperCase()
                          : "N/A"
                      }
                    />
                    {i < item.invoices.length - 1 && ", "}
                  </span>
                ))
              : "N/A",

            car_number: item?.car_number,
            total_amount: formatAmount(item.total_amount),
            negotiated_amount: formatAmount(item.negotiated_amount),
            amount_paid: item?.consolidated_invoice_details?.amount_paid
              ? formatAmount(item?.consolidated_invoice_details.amount_paid)
              : "N/A",
            reason:
              item?.reason === "Other" || item?.reason === "Write Off"
                ? item?.other_reason
                : item?.reason,
            status: <StatusTag status={item?.status} />,
            payment_status: item?.consolidated_invoice_details ? (
              <PaymentStatusTag
                status={item?.consolidated_invoice_details?.payment_status}
              />
            ) : (
              "N/A"
            ),
            created_by: item?.created_by_name,
            created_at: formatDateTime(item.created_at),
            updated_at: formatDateTime(item?.updated_at),
          },
        },
      ],
    };
  };

  const tableTitle = "Negotiations";

  return { tableColumns, tableData, viewData, tableTitle };
};

export const useNegotiationsForm = ({ updateFormValues, form }) => {
  const { loadData, loading, data: paymentModes } = useGet();
  const permissions = useGetUserPermissions();

  useEffect(() => {
    loadData({
      api: API_PAYMENT_MODES,
      params: {
        page_size: 30,
      },
    });
  }, []);
  const location = useLocation();
  const { selectedInvoices, carNumber } = location.state || {};

  useEffect(() => {
    if (updateFormValues) {
      // updateFormValues();

      updateFormValues({
        amount: form.amount,
      });
    }
  }, [updateFormValues]);
  const tableColumns = [
    createColumn("invoice_number", false, "Invoice Number"),
    createColumn("car_number", false, "Car Number"),
    createColumn("amount", false, "Amount"),
    createColumn("amount_paid", false, "Amount Paid"),
    createColumn("total_due", false, "Total Due"),
    createColumn("payment_status", false, "Payment Status"),
    createColumn("generated_at", false, "Generated At"),
  ];

  const tableData = selectedInvoices?.map((invoice, index) => ({
    ...invoice,
    amount: parseFloat(invoice.amount || 0).toLocaleString(),
    amount_paid: parseFloat(invoice.amount_paid || 0).toLocaleString(),
    total_due: parseFloat(invoice.total_due || 0).toLocaleString(),
    payment_status: <PaymentStatusTag status={invoice.payment_status} />,
    index: index + 1,
  }));

  const totalBalance = selectedInvoices?.reduce(
    (sum, inv) => sum + parseFloat(inv.total_due || "0"),
    0,
  );

  const invoiceIds = selectedInvoices?.map((inv) => inv.id) || [];

  const tableTotals = {
    total_due: formatAmount(totalBalance),
  };

  const sections: IFormSection[] = [
    {
      title: "Selected Invoices",
      fields: [],
      component: (
        <>
          <div></div>
          <Table
            key="selected-invoices"
            count={tableData.length}
            tableColumns={tableColumns ? tableColumns : []}
            tableData={tableData}
            loading={false}
            tableTotals={tableTotals}
          />
        </>
      ),
    },

    {
      title: "Payment Details",
      fields: [
        {
          field_name: "total_ssessed_amount",
          label: "Total Assessed Amount",
          type: "number",
          value: totalBalance,
          props: {
            required: false,
            disabled: true,
          },
        },
        {
          field_name: "payment_type",
          label: "Payment Type",
          type: "select",
          props: {
            required: false,
            placeholder: "Select payment type",
            options: [
              { value: "Full", label: "Full Payment" },
              { value: "Partial", label: "Partial Payment" },
              ...(itemExists(permissions, [CAN_REQUEST_NEGOTIATED_PAYMENT])
                ? [{ value: "Negotiated", label: "Negotiated Payment" }]
                : []),
            ],
          },
        },
        {
          field_name: "invoice",
          label: "Invoice (You can only pay for one invoice partially)",
          type: "select",
          loading: loading,
          options_data: selectedInvoices?.map((account: any) =>
            addSelectItem({
              label: account.invoice_number,
              value: account.id,
            }),
          ),
          props: {
            required: false,
            placeholder: "Select invoice to pay for",
          },
          visible: form.payment_type === "Partial",
        },
        {
          field_name: "payment_mode",
          label: "Payment Mode",
          type: "select",
          loading: loading,
          options_data: paymentModes?.results?.map((account: any) =>
            addSelectItem({
              label: account.description,
              value: account.mode,
            }),
          ),
          props: {
            required: false,
            placeholder: "Select payment mode",
          },
          visible:
            form.payment_type === "Full" || form.payment_type === "Partial",
        },
        {
          field_name: "phone_number",
          label: "Phone Number",
          type: "tel",
          props: {
            required: false,
            disabled: true,
          },
          visible: form.payment_mode === MOBILE_MONEY,
          // (form.payment_type === "Full" || form.payment_type === "Partial"),
        },

        {
          field_name: "amount",
          label: "Amount",
          type: "number",
          props: {
            required: true,
            placeholder: "Enter Amount",
          },
          visible: form.payment_type === "Partial",
        },
        {
          field_name: "reason",
          label: "Reason For Negotiation",
          type: "select",
          props: {
            required: false,
            placeholder: "Select reason",
            options: [
              { value: "Good Will", label: "Good Will" },
              { value: "Hardship", label: "Hardship" },
              { value: "Write Off", label: "Write Off" },
              { value: "Other", label: "Other" },
            ],
          },
          visible: form.payment_type === "Negotiated",
        },
        {
          field_name: "negotiated_amount",
          label: "Negotiated Amount",
          type: "number",
          props: {
            required: true,
            placeholder: "Enter Negotiated Amount",
          },
          visible:
            form.payment_type === "Negotiated" && form.reason !== "Write Off",
        },
        {
          field_name: "other_reason",
          label: "Other Reason",
          type: "text",
          props: {
            required: false,
            placeholder: "Enter Other Reason",
          },
          visible: form.reason === "Other" || form.reason === "Write Off",
        },
      ],
    },
  ];

  const title = "Invoice Payments";
  const redirect = getRegister(URL_NEGOTIATED_PAYMENTS);

  const onSubmit = (payload: any) => {
    if (
      payload.payment_type === "Negotiated" &&
      payload.reason === "Write Off"
    ) {
      payload.negotiated_amount = 0;
    }
    return {
      api:
        payload.payment_type === "Full"
          ? API_FULL_PAYMENTS
          : payload.payment_type === "Partial"
            ? API_COLLECT_PAYMENT
            : API_NEGOTIATIONS,
      // params: {
      //   ...payload,
      //   car_number: carNumber,
      //   invoices: invoiceIds,
      //   amount: payload.payment_type === "Full" ? totalBalance : payload.amount,
      // },
      params: {
        ...payload,
        car_number: carNumber,
        // Use invoice_ids for Negotiated Payments, otherwise use invoices
        ...(payload.payment_type === "Negotiated"
          ? { invoice_ids: invoiceIds }
          : { invoices: invoiceIds }),
        amount: payload.payment_type === "Full" ? totalBalance : payload.amount,
      },
    };
  };

  return {
    sections,
    onSubmit,
    title,
    redirect,
  };
};
